import { cn } from "@rivval/css";

interface PatternProps {
  variant?: "dots" | "crosses" | "glitch" | "hex" | "matrix" | "circuit";
  className?: string;
  size?: "sm" | "md" | "lg";
  color?: string;
  gap?: "sm" | "md" | "lg";
}

export function Pattern({
  variant = "dots",
  className,
  size = "md",
  color = "#FFFFFF",
  gap = "md",
}: PatternProps) {
  // Size mappings
  const sizeMap = {
    sm: {
      dots: "4px",
      crosses: "12px",
      glitch: "20px",
      hex: "16px",
      matrix: "20px",
      circuit: "24px",
    },
    md: {
      dots: "8px",
      crosses: "16px",
      glitch: "30px",
      hex: "24px",
      matrix: "30px",
      circuit: "32px",
    },
    lg: {
      dots: "12px",
      crosses: "24px",
      glitch: "40px",
      hex: "32px",
      matrix: "40px",
      circuit: "48px",
    },
  };

  // Gap mappings
  const gapMap = {
    sm: 1,
    md: 2,
    lg: 3,
  };

  // Pattern styles
  const patterns = {
    dots: {
      backgroundImage: `radial-gradient(${color} 2px, transparent 2px)`,
      backgroundSize: `calc(${sizeMap[size].dots} * ${gapMap[gap]}) calc(${sizeMap[size].dots} * ${gapMap[gap]})`,
    },
    crosses: {
      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='${
        Number.parseInt(sizeMap[size].crosses) * gapMap[gap]
      }' height='${
        Number.parseInt(sizeMap[size].crosses) * gapMap[gap]
      }' viewBox='0 0 ${12 * gapMap[gap]} ${12 * gapMap[gap]}'%3E%3Cpath d='M${5 * gapMap[gap]} 0h2v1H${
        5 * gapMap[gap]
      }zM${5 * gapMap[gap]} ${11 * gapMap[gap]}h2v1H${5 * gapMap[gap]}zM0 ${5 * gapMap[gap]}h1v2H0zM${
        11 * gapMap[gap]
      } ${5 * gapMap[gap]}h1v2h-1zM${4.5 * gapMap[gap]} ${4.5 * gapMap[gap]}h3v3h-3z' fill='${encodeURIComponent(
        color,
      )}' fill-opacity='0.3'/%3E%3C/svg%3E")`,
      backgroundSize: `calc(${sizeMap[size].crosses} * ${gapMap[gap]}) calc(${sizeMap[size].crosses} * ${gapMap[gap]})`,
    },
    glitch: {
      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='${
        Number.parseInt(sizeMap[size].glitch) * gapMap[gap]
      }' height='${
        Number.parseInt(sizeMap[size].glitch) * gapMap[gap]
      }' viewBox='0 0 ${20 * gapMap[gap]} ${20 * gapMap[gap]}'%3E%3Cpath d='M0 0h${20 * gapMap[gap]}v${
        2 * gapMap[gap]
      }H0zM0 ${6 * gapMap[gap]}h${20 * gapMap[gap]}v${1 * gapMap[gap]}H0zM0 ${10 * gapMap[gap]}h${
        20 * gapMap[gap]
      }v${3 * gapMap[gap]}H0zM0 ${16 * gapMap[gap]}h${20 * gapMap[gap]}v${4 * gapMap[gap]}H0z' fill='${encodeURIComponent(
        color,
      )}' fill-opacity='0.2'/%3E%3C/svg%3E")`,
      backgroundSize: `calc(${sizeMap[size].glitch} * ${gapMap[gap]}) calc(${sizeMap[size].glitch} * ${gapMap[gap]})`,
    },
    hex: {
      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='${
        Number.parseInt(sizeMap[size].hex) * gapMap[gap]
      }' height='${(Number.parseInt(sizeMap[size].hex) * Math.sqrt(3) * gapMap[gap]) / 2}' viewBox='0 0 ${24 * gapMap[gap]} ${
        (24 * Math.sqrt(3) * gapMap[gap]) / 2
      }'%3E%3Cpath d='M${12 * gapMap[gap]},0 L${22.5 * gapMap[gap]},${
        (6 * Math.sqrt(3) * gapMap[gap]) / 2
      } L${22.5 * gapMap[gap]},${(18 * Math.sqrt(3) * gapMap[gap]) / 2} L${12 * gapMap[gap]},${
        (24 * Math.sqrt(3) * gapMap[gap]) / 2
      } L${1.5 * gapMap[gap]},${(18 * Math.sqrt(3) * gapMap[gap]) / 2} L${1.5 * gapMap[gap]},${
        (6 * Math.sqrt(3) * gapMap[gap]) / 2
      } Z' fill='none' stroke='${encodeURIComponent(color)}' stroke-width='0.5' stroke-opacity='0.3'/%3E%3C/svg%3E")`,
      backgroundSize: `calc(${sizeMap[size].hex} * ${gapMap[gap]}) calc(${sizeMap[size].hex} * ${
        (Math.sqrt(3) / 2) * gapMap[gap]
      })`,
    },
    matrix: {
      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='${
        Number.parseInt(sizeMap[size].matrix) * gapMap[gap]
      }' height='${Number.parseInt(sizeMap[size].matrix) * gapMap[gap]}' viewBox='0 0 ${20 * gapMap[gap]} ${
        20 * gapMap[gap]
      }'%3E%3Ctext x='50%25' y='50%25' font-size='${
        14 * gapMap[gap]
      }' fill='${encodeURIComponent(color)}' fill-opacity='0.3' font-family='monospace' text-anchor='middle' dominant-baseline='middle'%3E01%3C/text%3E%3C/svg%3E")`,
      backgroundSize: `calc(${sizeMap[size].matrix} * ${gapMap[gap]}) calc(${sizeMap[size].matrix} * ${gapMap[gap]})`,
    },
    circuit: {
      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='${
        Number.parseInt(sizeMap[size].circuit) * gapMap[gap]
      }' height='${Number.parseInt(sizeMap[size].circuit) * gapMap[gap]}' viewBox='0 0 ${32 * gapMap[gap]} ${
        32 * gapMap[gap]
      }'%3E%3Cpath d='M${16 * gapMap[gap]} ${4 * gapMap[gap]}v${12 * gapMap[gap]}M${4 * gapMap[gap]} ${
        16 * gapMap[gap]
      }h${12 * gapMap[gap]}M${28 * gapMap[gap]} ${16 * gapMap[gap]}h-${12 * gapMap[gap]}M${16 * gapMap[gap]} ${
        28 * gapMap[gap]
      }v-${12 * gapMap[gap]}' stroke='${encodeURIComponent(
        color,
      )}' stroke-width='0.5' stroke-opacity='0.3'/%3E%3Ccircle cx='${16 * gapMap[gap]}' cy='${
        16 * gapMap[gap]
      }' r='1.5' fill='${encodeURIComponent(color)}' fill-opacity='0.3'/%3E%3C/svg%3E")`,
      backgroundSize: `calc(${sizeMap[size].circuit} * ${gapMap[gap]}) calc(${sizeMap[size].circuit} * ${gapMap[gap]})`,
    },
  };

  return (
    <div
      className={cn("h-full w-full", className)}
      style={{
        ...patterns[variant],
      }}
    />
  );
}
